<template>
  <div class="pa-4 pa-md-6">
    <div class="grey--text" style="font-size: 13px">Players</div>
    <h2 class="font-weight-bold mb-3 mb-md-5">Player List Duplicates</h2>
    <v-card class="shadow">
      <div class="d-flex justify-space-between align-center bs-py-3 bs-px-4">
        <div class="d-flex">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                @click="getPlayers"
              >
                <v-icon>mdi-cached</v-icon>
              </v-btn>
            </template>
            <span>Refresh</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <downloadexcel
                ref="excelRef"
                :data="excelData"
                :fields="excelFields"
                type="csv"
                :name="excelFileName"
                style="display: none"
              />
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                @click="exportExcel"
                :loading="isExporting"
              >
                <v-icon>mdi-file-delimited-outline</v-icon>
              </v-btn>
            </template>
            <span>Export to EXCEL</span>
          </v-tooltip>
        </div>
        <div class="d-flex" style="align-items: center; gap: 1rem">
          <v-text-field
            v-model="search"
            placeholder="Search"
            single-line
            hide-details="auto"
            :disabled="loading"
            clearable
            outlined
            dense
            @input="handleSearch"
          >
          </v-text-field>
        </div>
      </div>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="records"
          :loading="loading"
          loading-text="Loading, please wait a moment"
          :items-per-page="itemsPerPage"
          :server-items-length="totalRecords"
          @update:page="onPageChange"
          @update:items-per-page="onItemsPerPageChange"
          :footer-props="{
            'items-per-page-options': itemsPerPageOptions,
          }"
        >
          <template v-slot:[`item.action`]="{ item }">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  @click="showDetails(item.id)"
                  icon
                  small
                  color="primary"
                >
                  <v-icon size="large"> mdi-text-box-search-outline </v-icon>
                </v-btn>
              </template>
              <span>Details</span>
            </v-tooltip>
          </template>

          <template v-slot:[`item.mobile_number`]="{ item }">
            +63{{ item.mobile_number }}
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <span>
              {{ item.first_name }}
              {{ item.middle_name ? item.middle_name : "" }}
              {{ item.last_name }}
            </span>
          </template>

          <template v-slot:[`item.account_verified`]="{ item }">
            <v-chip
              small
              :class="
                item.account_verified == 'Verified'
                  ? 'green lighten-5 green--text'
                  : item.account_verified == 'Pending'
                  ? 'orange lighten-5 orange--text'
                  : item.account_verified == 'Rejected'
                  ? 'red lighten-5 red--text'
                  : 'blue lighten-5 blue--text'
              "
            >
              {{ item.account_verified }}
            </v-chip>
          </template>

          <template v-slot:[`item.verified_at`]="{ item }">
            <span>{{ formatDate(item.verified_at) }}</span>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span>{{ formatDate(item.created_at) }}</span>
          </template>

          <template v-slot:[`item.others`]="{ item }">
            <PlayerOtherDetails
              :data="{
                marital_status: item.marital_status,
                birthdate: item.birthdate,
                nationality: item.nationality,
                source_of_income: item.source_of_income,
                nature_of_work: item.nature_of_work,
                nature_of_business: item.nature_of_business,
                address: item.address,
                account_status: item.account_status,
                admin_reason: item.admin_deactivate_reason,
                player_reason: item.player_deactivate_reason,
                active_status: item.is_active,
              }"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import formRules from "@/helpers/formRules";
import downloadexcel from "vue-json-excel";
import formatDatev2 from "@/helpers/formatDatev2";
import PlayerOtherDetails from "@/components/players/PlayersOthers";
import api from "@/utils/api";
import { mapActions } from "vuex";

export default {
  components: { downloadexcel, PlayerOtherDetails },
  data: () => ({
    excelFileName: "export.csv",
    excelData: [],
    excelFields: {
      "PLAYER ID": "id",
      "FIRST NAME": "first_name",
      "MIDDLE NAME": "middle_name",
      "LAST NAME": "last_name",
      "MOBILE NUMBER": {
        callback: (value) => {
          return `+63${value.mobile_number}`;
        },
      },
      EMAIL: "email_address",
      "MARITAL STATUS": "marital_status",
      BIRTHDATE: "birthdate",
      BIRTHPLACE: "birthplace",
      NATIONALITY: "nationality",
      ADDRESS: "address",
      "CURRENT ADDRESS": "curr_address",
      "SOURCE OF INCOME": "source_of_income",
      "NATURE OF WORK": "nature_of_work",
      "NATURE OF BUSINESS": "nature_of_business",
      "VERIFICATION STATUS": "account_verified",
      "VERIFICATION DATE": {
        callback: (value) => {
          return formatDatev2(value.verified_at);
        },
      },
      "ACCOUNT STATUS": "is_active",
      "REGISTERED DATE": {
        callback: (value) => {
          return formatDatev2(value.created_at);
        },
      },
    },
    records: [],
    totalRecords: 0,
    loading: true,
    isExporting: false,
    accountStatus: "Active",
    verificationStatus: "Verified",
    search: "",
    page: 1,
    timeout: null,
    itemsPerPage: 10,
    itemsPerPageOptions: [10, 25, 50, 100],
    rules: formRules,
    menuFrom: false,
    menuTo: false,
    dateFrom: "2022-07-01",
    dateTo: new Date().toISOString().slice(0, 10),
    filterDialog: false,

    headers: [
      { text: "ID", value: "id", class: "text-uppercase" },
      {
        text: "Action",
        sortable: false,
        value: "action",
        class: "text-uppercase",
      },
      { text: "Name", value: "name", class: "text-uppercase" },
      {
        text: "Mobile Number",
        sortable: false,
        value: "mobile_number",
        class: "text-uppercase",
      },
      {
        text: "Email Address",
        sortable: false,
        value: "email_address",
        class: "text-uppercase",
      },
      {
        text: "Verification Status",
        value: "account_verified",
        class: "text-uppercase",
      },
      {
        text: "Verified date",
        value: "verified_at",
        class: "text-uppercase",
      },
      { text: "Registered Date", value: "created_at", class: "text-uppercase" },
      {
        text: "Others",
        sortable: false,
        value: "others",
        class: "text-uppercase",
      },
    ],
  }),

  mounted() {
    this.getPlayers();
  },

  methods: {
    ...mapActions("logout", ["sendLogoutRequest"]),

    async getPlayers() {
      this.loading = true;
      try {
        const { data } = await api.get(`/players/${this.$route.params.id}/duplicates`, {
          params: {
            limit: this.itemsPerPage,
            page: this.page,
            search: this.search,
            paginate: true,
          },
        });
        this.records = data.records;
        this.totalRecords = data.total_records;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.page = page;
      this.getPlayers();
    },

    onItemsPerPageChange(itemsPerPage) {
      this.page = 1;
      this.itemsPerPage = itemsPerPage;
      this.getPlayers();
    },

    handleSearch() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.page = 1;
        this.getPlayers();
      }, 500);
    },

    showDetails(id) {
      const url = this.$router.resolve({
        name: "PlayerDetails",
        params: { id },
      }).href;

      window.open(url, "_blank");
    },

    formatDate(date) {
      return formatDatev2(date);
    },

    async exportExcel() {
      this.isExporting = true;
      try {
        const { data } = await api.get("/players/74078/duplicates", {
          params: {
            search: this.search,
            status: this.accountStatus,
            account_verified: this.verificationStatus,
            date_from: this.dateFrom,
            date_to: this.dateTo,
            paginate: false,
          },
        });
        this.excelData = data;
        const today = new Date().toISOString().slice(0, 10);
        this.excelFileName = `players-duplicate-report-${today}.csv`;
        this.$nextTick(() => {
          this.$refs.excelRef.generate();
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>
